import { useState, useEffect } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { overlayBackdrop } from "../../theme/styles";
import { dbGetLoginAlerts, dbDismissLoginAlert, dbUnlockLogin } from "../../db/lockout";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

function relativeTime(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour${Math.floor(diff / 3600) !== 1 ? "s" : ""} ago`;
  return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) !== 1 ? "s" : ""} ago`;
}

export function LoginAlertsModal({ onClose }) {
  const { SA, GLASS, tokens, isDark, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dbGetLoginAlerts().then(rows => { setItems(rows); setLoading(false); });
  }, []);

  const handleUnlock = async (item) => {
    await dbUnlockLogin(item.email);
    await dbDismissLoginAlert(item.id);
    setItems(prev => prev.filter(i => i.id !== item.id));
  };

  const handleDismiss = async (item) => {
    await dbDismissLoginAlert(item.id);
    setItems(prev => prev.filter(i => i.id !== item.id));
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200, display: "flex", alignItems: isWide ? "center" : "flex-end", justifyContent: isWide ? "center" : "stretch", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal, borderRadius: isWide ? 16 : "16px 16px 0 0", width: isWide ? 520 : "100%", maxHeight: isWide ? "80vh" : "88vh", display: "flex", flexDirection: "column", boxSizing: "border-box", animation: isWide ? "modalSlideUp 0.25s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)", ...dragStyle }}>
        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}` }}>
          <span style={{ fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.2em", textTransform: "uppercase" }}>Security Alerts</span>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "12px 20px 30px" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.label, letterSpacing: "0.2em", padding: "40px 0", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>LOADING...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.label, letterSpacing: "0.2em", padding: "40px 0", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>NO LOCKOUT EVENTS</div>
          ) : items.map(item => (
            <div key={item.id} style={{ background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)", borderRadius: RADIUS.md, padding: "14px 16px", marginBottom: 10, border: `1px solid rgba(200,60,60,0.3)` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", color: "#D46060", background: "rgba(180,50,50,0.18)", padding: "2px 8px", borderRadius: 2, border: "1px solid rgba(200,60,60,0.35)" }}>
                    {item.airline || "UNKNOWN"}
                  </span>
                  <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.borderGray, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.06em" }}>
                    {relativeTime(item.triggered_at)}
                  </span>
                </div>
                <Button variant="ghost" onClick={() => handleDismiss(item)} style={{ color: SA.borderGray, fontSize: TYPOGRAPHY.sub, padding: "0 2px", lineHeight: 1 }}>×</Button>
              </div>
              <div style={{ fontSize: TYPOGRAPHY.note, color: SA.white, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.04em", marginBottom: 12 }}>{item.email}</div>
              <Button onClick={() => handleUnlock(item)} style={{ background: "none", border: `1px solid ${SA.borderGray}`, borderRadius: 3, color: SA.silver, fontSize: TYPOGRAPHY.tiny, padding: "4px 12px", fontFamily: "'Helvetica Neue',Arial,sans-serif", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Unlock Account
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
